import React, { useState, useRef, useCallback } from 'react';
import { Form } from 'react-bootstrap';
import imageCompression from 'browser-image-compression';
import secureApi from '../../utils/secureApi';
import { getImageUrl } from '../../utils/imageUrl';
import './LogoUpload.css';

export interface LogoUploadProps {
  currentLogo?: string;
  onLogoChange: (logoUrl: string) => void;
  onLogoRemove?: () => void;
  isEditMode?: boolean;
  context?: string; // For S3 path organization
  maxSizeMB?: number;
  maxWidthOrHeight?: number;
  className?: string;
  size?: number; // Logo size in pixels
  title?: string;
  label?: string;
  required?: boolean;
  compact?: boolean; // For form layouts
}

const LogoUpload: React.FC<LogoUploadProps> = ({
  currentLogo,
  onLogoChange,
  onLogoRemove,
  isEditMode = false,
  context = 'tournaments/logo',
  maxSizeMB = 1,
  maxWidthOrHeight = 800,
  className = '',
  size = 60,
  title = 'Logo',
  label = 'Tournament Logo',
  required = false,
  compact = false
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateImage = (file: File): string | null => {
    if (!file.type.startsWith('image/')) {
      return 'Please select a valid image file.';
    }
    
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      return 'Image size should be less than 5MB.';
    }

    return null;
  };

  const uploadImageToS3 = async (file: File): Promise<string> => {
    // Compress the image
    const compressedFile = await imageCompression(file, {
      maxSizeMB,
      maxWidthOrHeight,
      useWebWorker: true,
      onProgress: (progress) => setUploadProgress(progress)
    });

    // Generate file name and context
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    const ext = compressedFile.type.split('/')[1] || 'jpg';
    const fileName = `logo_${Date.now()}.${ext}`;
    const s3Context = `${context}/${dd}-${mm}-${yyyy}`;

    // Get presigned URL
    const presignRes = await secureApi.post(
      `/api/v1/general/presigned-url`,
      {
        fileName,
        contentType: compressedFile.type,
        context: s3Context,
      }
    );

    const presignedUrl = presignRes.data?.presignedUrl;
    const s3Key = presignRes.data?.key;
    
    if (!presignedUrl || !s3Key) {
      throw new Error('Failed to get presigned URL');
    }

    // Upload to S3
    const uploadRes = await fetch(presignedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': compressedFile.type },
      body: compressedFile,
    });
    
    if (!uploadRes.ok) {
      throw new Error('Failed to upload image to S3');
    }

    return s3Key;
  };

  const handleLogoClick = () => {
    if (isEditMode && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleLogoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validationError = validateImage(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Upload to S3
      const logoUrl = await uploadImageToS3(file);
      
      // Call parent callback with the S3 key
      onLogoChange(logoUrl);
      
    } catch (err) {
      console.error('Error uploading logo:', err);
      setError('Logo upload failed. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleRemoveLogo = () => {
    if (onLogoRemove) {
      onLogoRemove();
    }
  };

  const displayLogo = getImageUrl(currentLogo);

  return (
    <Form.Group className={`mb-3 ${className}`}>
      <Form.Label>
        {label}
        {required && <span className="text-danger">*</span>}
      </Form.Label>
      <div className={`player-profile-pic-upload-section ${compact ? 'compact' : ''}`}>
        <div className="player-profile-pic-upload">
          <div 
            className={`profile-pic-preview ${!isEditMode ? 'disabled' : ''}`}
            style={{
              width: `${size}px`,
              height: `${size}px`,
              backgroundColor: displayLogo ? 'transparent' : '#f8f9fa'
            }}
            onClick={isEditMode ? handleLogoClick : undefined}
          >
            {displayLogo && !isUploading && (
                <img 
                  src={displayLogo} 
                  alt={title}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '10px',
                    display: 'block'
                  }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
            )}
            {!displayLogo && !isUploading && (
              <div className="upload-placeholder">
                <i className="ri-image-line"></i>
                <div className="small">Upload {title}</div>
              </div>
            )}
            {isUploading && (
              <div className="upload-overlay">
                <div className="spinner-border spinner-border-sm text-light" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div className="small mt-2 text-light">Uploading... {Math.round(uploadProgress)}%</div>
              </div>
            )}
          </div>
          {displayLogo && isEditMode && !isUploading && (
            <div 
              className="edit-icon"
              onClick={handleLogoClick}
            >
              <i className="ri-pencil-line"></i>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleLogoChange}
            style={{ display: 'none' }}
            disabled={isUploading}
          />
        </div>
        
        {/* Logo upload error */}
        {error && (
          <div className="document-upload-error">
            <i className="ri-error-warning-line"></i>
            {error}
          </div>
        )}
        
        {/* Upload hint */}
        {isEditMode && (
          <div className={`upload-hint ${compact ? 'compact' : ''}`}>
            <small className="text-muted">
              <i className="ri-information-line me-1"></i>
              Supported formats: JPG, PNG, GIF. Recommended: Square image for best results.
            </small>
          </div>
        )}
      </div>
    </Form.Group>
  );
};

export default LogoUpload;
